import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminGetTickerStatus } from '../../api/admin';
import type { TickerStatus } from '../../api/admin';
import { useToastStore } from '../../store/useToastStore';
import { TH, PERIOD_LABELS, usePaginated, PaginationBar, fmtDuration } from './shared';

// Eigener Menüpunkt für die Auto-Aktualisierungs-Läufe: Zähler seit Start,
// Zeitplan, und die Lauf-Historie (~2h) mit Filtern und Pagination.

type RunFilter = 'all' | 'withNew' | 'withPush';

export default function MonitoringTab() {
  const addToast = useToastStore((s) => s.addToast);
  const [status, setStatus] = useState<TickerStatus | null>(null);
  const [runFilter, setRunFilter] = useState<RunFilter>('all');

  const load = useCallback(async () => {
    try {
      setStatus(await adminGetTickerStatus());
    } catch {
      addToast('Fehler beim Laden des Monitorings.', 'danger');
    }
  }, [addToast]);

  useEffect(() => {
    load();
    const id = setInterval(load, 5_000);
    return () => clearInterval(id);
  }, [load]);

  const filtered = useMemo(() => {
    const history = status?.history ?? [];
    if (runFilter === 'withNew') return history.filter((r) => r.newRows > 0);
    if (runFilter === 'withPush') return history.filter((r) => r.broadcasts.length > 0);
    return history;
  }, [status, runFilter]);

  const pag = usePaginated(filtered, 25);

  if (!status) return <div className="text-center py-5"><span className="spinner-border" /></div>;

  const now = Date.now();
  const fmtTime = (ms: number) => new Date(ms).toLocaleTimeString('de-DE');
  const uptimeMin = Math.round((now - status.stats.startedAt) / 60000);

  const FILTERS: { id: RunFilter; label: string }[] = [
    { id: 'all', label: `Alle (${status.history.length})` },
    { id: 'withNew', label: `Mit neuen Anrufen (${status.history.filter((r) => r.newRows > 0).length})` },
    { id: 'withPush', label: `Mit Push (${status.history.filter((r) => r.broadcasts.length > 0).length})` },
  ];

  return (
    <>
      {/* Übersicht */}
      <div className="row g-3 mb-4">
        {[
          { label: 'Nächster Schnell-Lauf', value: `${Math.max(0, Math.round((status.nextTickAt - now) / 1000))}s`, sub: `Takt ${Math.round(status.fastTickMs / 1000)}s · Dieser Monat jede ${status.thisMonthEveryNTicks}. Runde` },
          { label: 'Läufe seit Start', value: status.stats.totalRuns.toLocaleString('de-DE'), sub: `Backend läuft seit ${uptimeMin < 90 ? `${uptimeMin} Min.` : `${Math.round(uptimeMin / 60)} Std.`}` },
          { label: 'Neue Anrufe erkannt', value: `+${status.stats.totalNewRows.toLocaleString('de-DE')}`, sub: `${status.stats.totalBroadcasts} Push(es) an Clients` },
          {
            label: 'Voll-Lauf', value: status.lastFullRun ? fmtTime(status.lastFullRun.at) : '—',
            sub: status.lastFullRun
              ? `${(status.lastFullRun.durationMs / 1000).toFixed(1)}s (${status.lastFullRun.reason}) · nächster in ~${Math.max(0, Math.round((status.nextFullRunAt - now) / 60000))} Min.`
              : `nächster in ~${Math.max(0, Math.round((status.nextFullRunAt - now) / 60000))} Min.`,
          },
        ].map(({ label, value, sub }) => (
          <div key={label} className="col-6 col-xl-3">
            <div className="border rounded-3 p-3 h-100" style={{ background: 'var(--bs-tertiary-bg)' }}>
              <div className="text-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
              <div className="fs-4 fw-bold">{value}</div>
              <div className="text-muted" style={{ fontSize: '0.72rem' }}>{sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <div className="btn-group btn-group-sm">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              className={`btn ${runFilter === f.id ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setRunFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-muted small ms-auto">Historie: die letzten ~2 Stunden · aktualisiert alle 5 Sekunden</span>
      </div>

      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.82rem' }}>
          <thead style={TH}>
            <tr>
              <th className="ps-3">Lauf</th>
              <th>Geprüft</th>
              <th className="text-end">Dauer</th>
              <th className="text-end">Neue Anrufe</th>
              <th className="pe-3">Push an Clients</th>
            </tr>
          </thead>
          <tbody>
            {pag.slice.map((r) => (
              <tr key={r.at} className={r.newRows > 0 ? 'table-success' : ''}>
                <td className="ps-3 text-nowrap">{fmtTime(r.at)}</td>
                <td>{r.periods.map((p) => PERIOD_LABELS[p] ?? p).join(' + ')}</td>
                <td className="text-end">{r.durationMs < 100 ? '<0,1s' : fmtDuration(r.durationMs)}</td>
                <td className="text-end fw-semibold">{r.newRows > 0 ? `+${r.newRows}` : <span className="text-muted">—</span>}</td>
                <td className="pe-3">
                  {r.broadcasts.length > 0
                    ? <span className="badge bg-info-subtle text-info-emphasis">calls_update: {r.broadcasts.map((p) => PERIOD_LABELS[p] ?? p).join(', ')}</span>
                    : <span className="text-muted">—</span>}
                </td>
              </tr>
            ))}
            {pag.slice.length === 0 && (
              <tr><td colSpan={5} className="text-center text-muted py-4">
                {runFilter === 'all' ? 'Noch keine Läufe seit dem Start.' : 'Keine Läufe mit diesem Filter.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <PaginationBar page={pag.page} totalPages={pag.totalPages} pageSize={pag.pageSize} total={pag.total} onPage={pag.setPage} onPageSize={pag.setPageSize} />
    </>
  );
}
